/**
 * Published On Field Controller
 *
 * Connects the publication date input to the date helper and a live hint. The helper opens in the global modal
 * frame prefilled from what is typed; applying it writes the composed value back into the input. The hint
 * under the input is a turbo frame that reloads the stored-context preview when the input changes.
 *
 * Events:
 *   - publication-date--helper:apply (window, detail.value): takes the value, closes the helper dialog
 *
 * @extends ApplicationController
 */

import ApplicationController from '../../../javascript/controllers/application_controller'

export default class extends ApplicationController {
  static targets = ['input', 'link', 'hint']
  static values = { helperUrl: String, previewUrl: String, helperId: String }

  /** Sends the value typed so far along when the helper link is followed. */
  openHelper() {
    this.linkTarget.href = this.urlWithValue(this.helperUrlValue).href
  }

  /** Asks for the hint of the current value; a blank value renders the bare marker. */
  refreshHint() {
    this.hintTarget.src = this.urlWithValue(this.previewUrlValue).href
  }

  /**
   * Drops the hint when it cannot be fetched; the model validation still speaks on save.
   * @param {Event} event
   */
  hintFailed(event) {
    event.preventDefault()
    this.hintTarget.removeAttribute('src')
    this.hintTarget.replaceChildren()
  }

  /**
   * A response without the hint frame means the endpoint refused or the frame id is wrong, which is a bug, not
   * a network failure: report it and degrade like a failed fetch.
   * @param {CustomEvent} event
   */
  hintMissing(event) {
    const { status, url } = event.detail.response
    this.reportError(new Error(`published on hint frame missing: ${status} ${url}`), {
      action: 'hintMissing',
    })
    this.hintFailed(event)
  }

  /**
   * Takes the value this field's helper announces instead of letting it submit anything; other helpers'
   * announcements pass through untouched.
   * @param {CustomEvent} event
   */
  apply(event) {
    if (!event.target.querySelector(`#${this.helperIdValue}-preview`)) return

    event.preventDefault()
    this.inputTarget.value = event.detail.value
    this.inputTarget.dispatchEvent(new Event('input', { bubbles: true }))
    this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))
    event.target.close()
    this.inputTarget.focus()
  }

  /**
   * @param {string} path
   * @returns {URL}
   */
  urlWithValue(path) {
    const url = new URL(path, window.location.origin)
    url.searchParams.set('value', this.inputTarget.value)
    return url
  }
}
